import { Button, Result } from "antd";
import { DataSyndicationSourceList, ErrorResult, LoadingOverlay, ManageDataSyndicationSource, OkCancelConfirmationBox, SuccessResult } from "components";
import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import actions from "store/actions";

import AppPaths from "constants/appPaths";
import flatten from "flat";
import _ from "lodash";
import { withRouter } from "react-router";

import { usePrevious } from "common/customHooks";
import { ActionStatus } from "common/enums";
import DataSyndicationSourceSubscription from "./DataSyndicationSourceSubscription";

function DataSyndicationSource({
    action,
    match,
    history,
    sourceId,
    permission,
    getDataSyndicationSourceList,
    getDataSyndicationSourceListAction,
    dataSyndicationSourceList,
    createDataSyndicationSource,
    createDataSyndicationSourceAction,
    updateDataSyndicationSource,
    updateDataSyndicationSourceAction,
    getDataSyndicationSource,
    getDataSyndicationSourceAction,
    deleteDataSyndicationSource,
    deleteDataSyndicationSourceAction,
    existingSource,
}) {
    const [busyMessage, setBusyMessage] = useState("Please wait...");
    const [result, setResult] = useState(null);
    const [mountingComponent, setMountingComponent] = useState(true);
    const [selectedSourceId, setSelectedSourceId] = useState(null);
    const [manageDataSyndicationSourceErrors, setManageDataSyndicationSourceErrors] = useState({});
    const [deleteConfirmation, setDeleteConfirmation] = useState({ visible: false, message: "", deleteItem: null });
    const previousCreateDataSyndicationSourceAction = usePrevious(createDataSyndicationSourceAction);
    const previousUpdateDataSyndicationSourceAction = usePrevious(updateDataSyndicationSourceAction);
    const previousDeleteDataSyndicationSourceAction = usePrevious(deleteDataSyndicationSourceAction);

    useEffect(() => {
        if (mountingComponent) {
            setMountingComponent(false);
            if (!action) {
                getDataSyndicationSourceList();
            } else {
                if (sourceId) {
                    getDataSyndicationSource(sourceId);
                }
            }
        }
    }, [mountingComponent, action, sourceId, getDataSyndicationSourceList, getDataSyndicationSource]);

    useEffect(() => {
        if (createDataSyndicationSourceAction && previousCreateDataSyndicationSourceAction && createDataSyndicationSourceAction !== previousCreateDataSyndicationSourceAction) {
            if (createDataSyndicationSourceAction.status === ActionStatus.FAILED) {
                if (createDataSyndicationSourceAction.fields) {
                    setManageDataSyndicationSourceErrors(createDataSyndicationSourceAction.fields);
                } else {
                    setResult({
                        success: false,
                        title: "Create Syndication Source",
                        message: createDataSyndicationSourceAction.message || "Syndication source cannot be created at this moment.",
                        onOkClicked: () => setResult(null),
                    });
                    setManageDataSyndicationSourceErrors({});
                }
            } else if (createDataSyndicationSourceAction.status === ActionStatus.SUCCESS) {
                setResult({
                    success: true,
                    title: "Create Syndication Source",
                    message: "Syndication source created successfully.",
                    onOkClicked: () => {
                        history.push(
                            AppPaths.TENANT_CONFIGURATION_ITEM.replace(":tenant", match.params.tenant).replace(":configurationItem", match.params.configurationItem.toLowerCase()),
                        );
                    },
                });
            }
        }
    }, [createDataSyndicationSourceAction, previousCreateDataSyndicationSourceAction, history, match]);

    useEffect(() => {
        if (updateDataSyndicationSourceAction && previousUpdateDataSyndicationSourceAction && updateDataSyndicationSourceAction !== previousUpdateDataSyndicationSourceAction) {
            if (updateDataSyndicationSourceAction.status === ActionStatus.FAILED) {
                if (updateDataSyndicationSourceAction.fields) {
                    setManageDataSyndicationSourceErrors(updateDataSyndicationSourceAction.fields);
                } else {
                    setResult({
                        success: false,
                        title: "Update Syndication Source",
                        message: updateDataSyndicationSourceAction.message || "Syndication source cannot be updated at this moment.",
                        onOkClicked: () => setResult(null),
                    });
                    setManageDataSyndicationSourceErrors({});
                }
            } else if (updateDataSyndicationSourceAction.status === ActionStatus.SUCCESS) {
                setResult({
                    success: true,
                    title: "Update Syndication Source",
                    message: "Syndication source updated successfully.",
                    onOkClicked: () => {
                        history.push(
                            AppPaths.TENANT_CONFIGURATION_ITEM.replace(":tenant", match.params.tenant).replace(":configurationItem", match.params.configurationItem.toLowerCase()),
                        );
                    },
                });
            }
        }
    }, [updateDataSyndicationSourceAction, previousUpdateDataSyndicationSourceAction, history, match]);

    useEffect(() => {
        if (deleteDataSyndicationSourceAction && previousDeleteDataSyndicationSourceAction && deleteDataSyndicationSourceAction !== previousDeleteDataSyndicationSourceAction) {
            if (deleteDataSyndicationSourceAction.status === ActionStatus.FAILED) {
                setResult({
                    success: false,
                    title: "Delete Syndication Source",
                    message: deleteDataSyndicationSourceAction.message || "Syndication source cannot be deleted at this moment.",
                    onOkClicked: () => {
                        setResult(null);
                        setDeleteConfirmation({
                            visible: false,
                            deleteItem: null,
                            message: "",
                        });
                    },
                });
            } else if (deleteDataSyndicationSourceAction.status === ActionStatus.SUCCESS) {
                setResult({
                    success: true,
                    title: "Delete Syndication Source",
                    message: `Syndication source '${deleteConfirmation.deleteItem.name}' deleted successfully.`,
                    onOkClicked: () => {
                        setResult(null);
                        setDeleteConfirmation({
                            visible: false,
                            deleteItem: null,
                            message: "",
                        });
                    },
                });
            }
        }
    }, [deleteConfirmation, deleteDataSyndicationSourceAction, previousDeleteDataSyndicationSourceAction, history, match]);

    const showAddSource = useCallback(() => {
        history.push(
            AppPaths.TENANT_CONFIGURATION_ITEM_ACTION.replace(":tenant", match.params.tenant)
                .replace(":configurationItem", match.params.configurationItem.toLowerCase())
                .replace(":itemAction", "create"),
        );
    }, [history, match]);

    const showViewSource = useCallback(
        (selectedSourceId) => {
            history.push(
                AppPaths.TENANT_CONFIGURATION_ITEM_ID_ACTION.replace(":tenant", match.params.tenant)
                    .replace(":configurationItem", match.params.configurationItem.toLowerCase())
                    .replace(":itemId", selectedSourceId)
                    .replace(":itemAction", "view"),
            );
        },
        [history, match],
    );

    const showEditSource = useCallback(
        (selectedSourceId) => {
            history.push(
                AppPaths.TENANT_CONFIGURATION_ITEM_ID_ACTION.replace(":tenant", match.params.tenant)
                    .replace(":configurationItem", match.params.configurationItem.toLowerCase())
                    .replace(":itemId", selectedSourceId)
                    .replace(":itemAction", "edit"),
            );
        },
        [history, match],
    );

    const createSource = useCallback(
        (source) => {
            setBusyMessage("Creating source...");
            createDataSyndicationSource(source);
        },
        [createDataSyndicationSource],
    );

    const goBackToSourceList = useCallback(() => {
        history.push(AppPaths.TENANT_CONFIGURATION_ITEM.replace(":tenant", match.params.tenant).replace(":configurationItem", match.params.configurationItem.toLowerCase()));
    }, [history, match]);

    const updateSource = useCallback(
        (updateSourceId, updatedSource) => {
            if (_.isEmpty(updatedSource) === false) {
                setBusyMessage("Updating source...");
                updateDataSyndicationSource(updateSourceId, updatedSource);
            } else {
                goBackToSourceList();
            }
        },
        [goBackToSourceList, updateDataSyndicationSource],
    );

    const onDeleteSourceCancel = useCallback(() => {
        setDeleteConfirmation({
            visible: false,
            deleteItem: null,
            message: "",
        });
    }, []);

    const onDeleteSourceConfirmed = useCallback(() => {
        setBusyMessage("Deleting source...");
        deleteDataSyndicationSource(deleteConfirmation.deleteItem.sourceId);
        setDeleteConfirmation({
            visible: false,
            deleteItem: deleteConfirmation.deleteItem,
            message: "",
        });
    }, [deleteConfirmation, deleteDataSyndicationSource]);

    const showDeleteConfirmationDialog = useCallback((item) => {
        setDeleteConfirmation({
            visible: true,
            message: `Are you sure, you want to delete source '${item.name}'?`,
            deleteItem: item,
        });
    }, []);

    const enableDisableSource = useCallback(() => {}, []);

    const onManageDataSyndicationSourceValuesChanged = useCallback(
        (changedValues, allValues) => {
            if (_.isEmpty(manageDataSyndicationSourceErrors) === false) {
                let formErrors = { ...manageDataSyndicationSourceErrors };
                let flatObject = flatten(changedValues);
                for (let key in flatObject) {
                    delete formErrors[key];
                }
                setManageDataSyndicationSourceErrors(formErrors);
            }
        },
        [manageDataSyndicationSourceErrors],
    );

    const isBusy = useCallback(() => {
        if (
            getDataSyndicationSourceListAction.status === ActionStatus.PENDING ||
            createDataSyndicationSourceAction.status === ActionStatus.PENDING ||
            updateDataSyndicationSourceAction.status === ActionStatus.PENDING ||
            getDataSyndicationSourceAction.status === ActionStatus.PENDING ||
            deleteDataSyndicationSourceAction.status === ActionStatus.PENDING
        ) {
            return true;
        }
        return false;
    }, [
        getDataSyndicationSourceListAction,
        createDataSyndicationSourceAction,
        updateDataSyndicationSourceAction,
        getDataSyndicationSourceAction,
        deleteDataSyndicationSourceAction,
    ]);

    const getComponentContent = useCallback(() => {
        if (!action) {
            return (
                <>
                    <OkCancelConfirmationBox
                        show={deleteConfirmation.visible}
                        message={deleteConfirmation.message}
                        onCancel={onDeleteSourceCancel}
                        onOk={onDeleteSourceConfirmed}
                    ></OkCancelConfirmationBox>
                    {getDataSyndicationSourceListAction.status !== ActionStatus.PENDING ? (
                        <div id="data-syndication-source-list-container" className="flex size-full flex-1 flex-col gap-2">
                            <div className="flex flex-1 flex-col justify-stretch overflow-hidden">
                                <DataSyndicationSourceList
                                    items={dataSyndicationSourceList.Items}
                                    onAddSource={showAddSource}
                                    onViewSource={showViewSource}
                                    onEditSource={showEditSource}
                                    onDeleteSource={showDeleteConfirmationDialog}
                                    onEnableDisableSource={enableDisableSource}
                                    selectedSourceId={selectedSourceId}
                                    onSourceSelected={setSelectedSourceId}
                                />
                            </div>
                            {selectedSourceId && (
                                <div className="relative flex flex-1 flex-col justify-stretch overflow-hidden">
                                    <DataSyndicationSourceSubscription readOnly={false} sourceId={selectedSourceId} />
                                </div>
                            )}
                        </div>
                    ) : (
                        <></>
                    )}
                </>
            );
        }

        switch (action.toLowerCase()) {
            case "create":
                return (
                    <ManageDataSyndicationSource
                        action={action}
                        permission={permission}
                        formErrors={manageDataSyndicationSourceErrors}
                        onValuesChanged={onManageDataSyndicationSourceValuesChanged}
                        onSave={createSource}
                        onCancel={goBackToSourceList}
                    ></ManageDataSyndicationSource>
                );
            case "view":
            case "edit":
                if (getDataSyndicationSourceAction.status === ActionStatus.SUCCESS) {
                    if (existingSource) {
                        return (
                            <ManageDataSyndicationSource
                                action={action}
                                permission={permission}
                                readOnlyView={action === "view"}
                                formErrors={manageDataSyndicationSourceErrors}
                                syndicationSourceVaue={existingSource}
                                onValuesChanged={onManageDataSyndicationSourceValuesChanged}
                                onSave={updateSource}
                                onCancel={goBackToSourceList}
                            ></ManageDataSyndicationSource>
                        );
                    } else {
                        return (
                            <Result
                                status="warning"
                                title="Syndication Source not found."
                                subTitle="The Syndication Source you are looking for does not exist."
                                extra={
                                    <Button type="primary" onClick={goBackToSourceList}>
                                        Go Back
                                    </Button>
                                }
                            />
                        );
                    }
                }
                return <></>;
            case "history":
                return <></>;
            default:
                return <></>;
        }
    }, [
        action,
        deleteConfirmation,
        onDeleteSourceCancel,
        onDeleteSourceConfirmed,
        getDataSyndicationSourceListAction,
        dataSyndicationSourceList,
        showAddSource,
        showViewSource,
        showEditSource,
        showDeleteConfirmationDialog,
        enableDisableSource,
        createSource,
        goBackToSourceList,
        getDataSyndicationSourceAction,
        existingSource,
        permission,
        onManageDataSyndicationSourceValuesChanged,
        manageDataSyndicationSourceErrors,
        updateSource,
        selectedSourceId,
    ]);

    return (
        <>
            {result && (
                <>
                    <SuccessResult show={result.success} title={result.title} subTitle={result.message} onOkClick={result.onOkClicked}></SuccessResult>
                    <ErrorResult show={!result.success} title={result.title} subTitle={result.message} onOkClick={result.onOkClicked}></ErrorResult>
                </>
            )}
            <LoadingOverlay busy={isBusy()} spinner message={busyMessage || "Please wait..."}></LoadingOverlay>
            {getComponentContent()}
        </>
    );
}

const mapStateToProps = (state, ownProps) => {
    return {
        getDataSyndicationSourceListAction: state.dataSyndicationSource.getDataSyndicationSourceListAction,
        dataSyndicationSourceList: state.dataSyndicationSource.dataSyndicationSourceList,
        createDataSyndicationSourceAction: state.dataSyndicationSource.createDataSyndicationSourceAction,
        getDataSyndicationSourceAction: state.dataSyndicationSource.getDataSyndicationSourceAction,
        existingSource: state.dataSyndicationSource.source,
        updateDataSyndicationSourceAction: state.dataSyndicationSource.updateDataSyndicationSourceAction,
        deleteDataSyndicationSourceAction: state.dataSyndicationSource.deleteDataSyndicationSourceAction,
        permission: { canEdit: true, canView: true, canDelete: true, canAdd: true },
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getDataSyndicationSourceList: () => dispatch(actions.getDataSyndicationSourceListRequest()),
        createDataSyndicationSource: (source) => dispatch(actions.createDataSyndicationSourceRequest(source)),
        getDataSyndicationSource: (sourceId) => dispatch(actions.getDataSyndicationSourceRequest(sourceId)),
        updateDataSyndicationSource: (sourceId, updatedSource) => dispatch(actions.updateDataSyndicationSourceRequest(sourceId, updatedSource)),
        deleteDataSyndicationSource: (sourceId) => dispatch(actions.deleteDataSyndicationSourceRequest(sourceId)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(DataSyndicationSource));
